/**
 * SFX rate limiter: stops rapid repeats (button mashing, many bricks landing
 * in one frame) from stacking into a loud, harsh smear.
 *
 * Two rules:
 *  1. Per sound: a minimum interval between two starts of the same sound.
 *  2. Global: at most `maxLowPriority` sounds may start within `windowMs`;
 *     only low-priority sounds (taps, ticks) are dropped by this rule, so an
 *     important cue (correct / wrong / level up) is never swallowed by UI noise.
 *
 * Time is supplied by the caller (the AudioContext clock), so this is pure
 * and deterministic (unit-tested in node).
 */

export type SfxPriority = 'high' | 'low';

export interface SfxRateRule {
  minIntervalMs: number;
  priority: SfxPriority;
}

export interface RateLimiterOptions {
  windowMs: number;
  maxLowPriority: number;
}

export const DEFAULT_RATE_LIMITS: Readonly<RateLimiterOptions> = { windowMs: 150, maxLowPriority: 4 };

export class SfxRateLimiter<N extends string> {
  private readonly lastStart = new Map<N, number>();
  private recent: number[] = [];

  constructor(
    private readonly rules: Readonly<Record<N, SfxRateRule>>,
    private readonly options: Readonly<RateLimiterOptions> = DEFAULT_RATE_LIMITS,
  ) {}

  /** True (and records the start) if `name` may play at `nowMs`. */
  allow(name: N, nowMs: number): boolean {
    const rule = this.rules[name];
    if (!rule || !Number.isFinite(nowMs)) return false;

    const last = this.lastStart.get(name);
    // A clock that went backwards (new AudioContext) resets the history.
    if (last !== undefined && nowMs < last) this.reset();
    else if (last !== undefined && nowMs - last < rule.minIntervalMs) return false;

    this.recent = this.recent.filter((t) => nowMs - t < this.options.windowMs && t <= nowMs);
    if (rule.priority === 'low' && this.recent.length >= this.options.maxLowPriority) return false;

    this.lastStart.set(name, nowMs);
    this.recent.push(nowMs);
    return true;
  }

  reset(): void {
    this.lastStart.clear();
    this.recent = [];
  }
}
